// app/javascript/controllers/hash_target_animation_controller.js
import { Controller } from "stimulus"
import { gsap } from "gsap"

export default class extends Controller {
  static values = {
    duration: { type: Number, default: 0.8 },
    // How many times to flash the outline
    repeat: { type: Number, default: 2 },
    // Outline highlight color (light green by default)
    highlightColor: { type: String, default: "rgb(225, 250, 209)" },
    // Outline width during highlight (in pixels)
    outlineWidth: { type: Number, default: 3 },
    // Delay before starting animation (allows page to settle)
    delay: { type: Number, default: 0.3 }
  }

  connect() {
    // Check if URL contains a hash
    if (window.location.hash) {
      const hashId = window.location.hash.substring(1); // Remove the # character

      // Determine which ID to check against the hash
      const elementId = this.element.id;

      if (elementId && hashId === elementId) {
        // Create the highlight overlay element
        this.createHighlightElement();

        setTimeout(() => {
          this.pulseHighlight();
        }, this.delayValue * 1000);
      }
    }
  }

  createHighlightElement() {
    // Check if we already have a highlight element
    if (this.element.querySelector('.hash-target-highlight')) {
      return;
    }

    // Create a highlight element that will be animated
    const highlight = document.createElement('div');
    highlight.className = 'hash-target-highlight';

    // Get the computed style of the element to match border radius
    const computedStyle = window.getComputedStyle(this.element);
    const elementBorderRadius = computedStyle.borderRadius;

    // Set the highlight element's initial style
    Object.assign(highlight.style, {
      position: 'absolute',
      top: '-3px',
      left: '-3px',
      right: '-3px',
      bottom: '-3px',
      pointerEvents: 'none',
      zIndex: 10,
      // Match the border radius of the parent element (if it has one)
      borderRadius: elementBorderRadius !== '0px' ? elementBorderRadius : '3px',
      border: `0px solid ${this.highlightColorValue}`,
      opacity: 1
    });

    // We need to set the element to position relative if it's not already positioned
    if (computedStyle.position === 'static') {
      this.element.style.position = 'relative';
    }

    // Add the highlight element to the target
    this.element.appendChild(highlight);

    // Store a reference to the highlight element
    this.highlightElement = highlight;
  }

  pulseHighlight() {
    if (!this.highlightElement) return;

    // Create a smooth pulse animation
    const timeline = gsap.timeline({
      repeat: this.repeatValue - 1,
      onComplete: () => {
        // Clean up when all animations are done
        if (this.highlightElement && this.highlightElement.parentNode) {
          this.highlightElement.parentNode.removeChild(this.highlightElement);
        }

        // Remove the hash from the URL without refreshing the page
        if (window.location.hash) {
          history.pushState("", document.title, window.location.pathname + window.location.search);
        }
      }
    });

    // Animate border width up and down smoothly
    timeline
      .fromTo(this.highlightElement,
        { borderWidth: 0 },
        {
          borderWidth: this.outlineWidthValue,
          duration: this.durationValue / 2,
          ease: "sine.inOut"
        }
      )
      .to(this.highlightElement, {
        borderWidth: 0,
        duration: this.durationValue / 2,
        ease: "sine.inOut"
      });

    // Play the animation
    timeline.play();
  }
}
